import { Button, Form } from 'antd';
import { $Formutil, withForm } from 'react-antd-formutil';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { BankCardItemTypes } from 'components/BankCardSelect';
import BottomBox from 'components/BottomBox';
import { CardButtonWrap } from 'components/Card';
import { PlaidBankCardItem, PlaidLink } from 'components/vendor/plaid';
import AuthMethod from 'modules/BankCards/components/AuthMethod';
import linkBankIcon from 'static/images/link-bank.svg';
import security from 'stores/security';
import { Source } from 'types/funding';
import './style.scss';

interface SelectWayProps {
    $formutil: $Formutil<{
        authMethod: string;
    }>;
}

const SelectWay: React.FC<SelectWayProps> = ({ $formutil }) => {
    const location = useLocation();
    const locationState = location.state as {
        source: Source;
        list?: PlaidBankCardItem[];
        id?: BankCardItemTypes['id'];
    } | null;
    const source = locationState?.source;
    const navigate = useNavigate();
    const { $params } = $formutil;
    const { authMethod } = $params;

    const handleSubmit = () => {
        navigate('../manual', {
            replace: true,
            state: locationState
        });
    };

    if (!source) {
        return <Navigate to="/profile" replace />;
    }

    return (
        <div className="bankcards-select-way-root">
            <h3 className="main-title">
                {__('Link your bank account')}
                <p className="desc">{__('Seamlessly connect your bank account with manual or automatic linkage')}</p>
            </h3>
            <img className="icon" src={linkBankIcon} alt="link-bank" />
            <Form layout="vertical" onFinish={handleSubmit}>
                <AuthMethod
                    list={[
                        ...(security.isMarscoAuditing
                            ? []
                            : [
                                  {
                                      title: __('Manual Linkage'),
                                      desc: [
                                          `1. ${__('Upload a photo of your bank statement or void check.')}`,
                                          `2. ${__('Our team will verify your account information promptly. ')}`
                                      ],
                                      value: 'manual'
                                  }
                              ]),
                        {
                            title: __('Automatic Linkage'),
                            desc: [
                                `1. ${__('Log into your online bank account through Plaid.')}`,
                                `2. ${__('Authorize connection to TradeUP instantly.')}`
                            ],
                            value: 'auto'
                        }
                    ]}
                    defaultValue="auto"
                />
                {authMethod === 'auto' && (
                    <div
                        className="tips"
                        dangerouslySetInnerHTML={{
                            __html: __(
                                "We use <b>Plaid</b>, to verify your bank account information, periodically check your balance to see if there's enough money to cover transactions, and as otherwise permitted under our <a href='https://www.tradeup.com/agreement/206' target='_blank' rel='noopener noreferrer'>Privacy Policy</a>. By continuing, you agree that your information will be accessed by Plaid and shared between TradeUP and Plaid in accordance with our Privacy Policy. You can turn off our use of it by removing permissions for this bank account in your TradeUP account settings."
                            )
                        }}
                    />
                )}
                <BottomBox className="mt-5">
                    <CardButtonWrap>
                        {authMethod === 'manual' ? (
                            <Button htmlType="submit" size="large" block type="primary">
                                {__('Get started!')}
                            </Button>
                        ) : (
                            <PlaidLink
                                btnProps={{
                                    type: 'primary',
                                    size: 'large'
                                }}
                                btnText={__('Get started!')}
                                source={source}
                                limit={
                                    locationState?.list &&
                                    locationState.list.filter(item => item.id_source === 'Fund').length >= 4
                                }
                                errorInBtn
                            />
                        )}
                    </CardButtonWrap>
                </BottomBox>
            </Form>
        </div>
    );
};

export default withForm(SelectWay);
